import { useEffect, useMemo } from 'react';
import {
  Dropdown,
  Field,
  FieldTypes,
  Form,
  InputStatuses,
  TextInput,
  useFormApi,
  useFormState,
} from '@carboledger/ui';
import { TextField } from '@/components/common';
import {
  CERTIFICATION_STATUS,
  buildCertificateTypeFilterOptions,
  buildCertificationStatusOptions,
  buildSiteTypeFilterOptions,
} from '@/config/statuses';
import { useGetIsccCertificateQuery } from '@/lib/api/lookupsApi';
import { useDebounce, useTranslations } from '@/hooks';
import { SITE_FIELDS } from '../../constants/sites.constants';

const IsccCertificateNumberField = () => {
  const t = useTranslations();
  const { values } = useFormState();
  const { setValue } = useFormApi();

  const typed = (values[SITE_FIELDS.isccCertificateNumber] ?? '').trim();
  const certificateNumber = useDebounce(typed);
  const { data, isFetching, isError } = useGetIsccCertificateQuery(certificateNumber, {
    skip: !certificateNumber,
  });

  useEffect(() => {
    if (!certificateNumber) {
      setValue(SITE_FIELDS.validFrom, '');
      setValue(SITE_FIELDS.validTo, '');
      return;
    }
    if (!data) return;
    setValue(SITE_FIELDS.validFrom, data.validFrom);
    setValue(SITE_FIELDS.validTo, data.validTo);
  }, [certificateNumber, data, setValue]);

  const lookupMessage = useMemo(() => {
    if (isFetching) return t('SITES_CERTIFICATE_LOOKUP_CHECKING');
    if (isError) return t('SITES_CERTIFICATE_LOOKUP_NOT_FOUND');
    return undefined;
  }, [isFetching, isError, t]);

  const lookupStatus = isError ? InputStatuses.error : InputStatuses.default;

  return (
    <Field name={SITE_FIELDS.isccCertificateNumber} required>
      {({ id, name, value, onChange, onBlur, status, message, required }) => (
        <TextInput
          id={id}
          name={name}
          label={t('FIELD_ISCC_CERTIFICATE_NUMBER')}
          placeholder={t('FORM_ISCC_CERTIFICATE_NUMBER_PLACEHOLDER')}
          value={value}
          onChange={onChange}
          onBlur={onBlur}
          status={message ? status : lookupStatus}
          message={message ?? lookupMessage}
          required={required}
        />
      )}
    </Field>
  );
};

export const SiteCertificateFields = () => {
  const t = useTranslations();
  const { values } = useFormState();

  const certificateTypeOptions = useMemo(() => buildCertificateTypeFilterOptions(t), [t]);
  const siteTypeOptions = useMemo(() => buildSiteTypeFilterOptions(t), [t]);
  const certificationStatusOptions = useMemo(() => buildCertificationStatusOptions(t), [t]);

  const isCertified = values[SITE_FIELDS.certificationStatus] === CERTIFICATION_STATUS.certified;

  return (
    <>
      <Form.Row columns={3}>
        <Field name={SITE_FIELDS.certificateType} required>
          {({ id, name, value, onChange, onBlur, status, message, required }) => (
            <Dropdown
              id={id}
              name={name}
              label={t('FIELD_CERTIFICATION_TYPE')}
              placeholder={t('FORM_SELECT_CERTIFICATION_TYPE')}
              value={value}
              onChange={onChange}
              onBlur={onBlur}
              status={status}
              message={message}
              required={required}
              options={certificateTypeOptions}
            />
          )}
        </Field>

        <Field name={SITE_FIELDS.siteTypes} type={FieldTypes.multi} required>
          {({ id, name, value, onChange, status, message, required }) => (
            <Dropdown
              multiple
              id={id}
              name={name}
              label={t('FIELD_SITE_TYPE')}
              placeholder={t('FORM_SELECT_SITE_TYPE')}
              value={value}
              onChange={onChange}
              status={status}
              message={message}
              required={required}
              options={siteTypeOptions}
            />
          )}
        </Field>

        <Field name={SITE_FIELDS.certificationStatus} required>
          {({ id, name, value, onChange, onBlur, status, message, required }) => (
            <Dropdown
              id={id}
              name={name}
              label={t('FIELD_CERTIFICATION_STATUS')}
              placeholder={t('FORM_SELECT_CERTIFICATION_STATUS')}
              value={value}
              onChange={onChange}
              onBlur={onBlur}
              status={status}
              message={message}
              required={required}
              options={certificationStatusOptions}
            />
          )}
        </Field>
      </Form.Row>

      {isCertified ? (
        <Form.Row columns={3}>
          <IsccCertificateNumberField />
          <TextField
            disabled
            name={SITE_FIELDS.validFrom}
            label={t('FIELD_VALID_FROM')}
            type="date"
          />
          <TextField
            disabled
            name={SITE_FIELDS.validTo}
            label={t('FIELD_VALID_TILL')}
            type="date"
          />
        </Form.Row>
      ) : null}
    </>
  );
};
